import { notifications } from '@mantine/notifications';
import { ResponseType } from '@microsoft/microsoft-graph-client';
import { extractGpsMetadataJson } from './imageUtils';
import type { LocationMetadata } from './types/LocationMetadata';

// @ts-ignore keep this here
/**
 * The name of the SharePoint document library for investment property files, read from an environment variable.
 */
export const documentLibraryName = import.meta.env.VITE_PRISM_INVEST_DOCUMENTLIBRARYNAME || '';


// Re-exported interface so all drive related components share the same shape
/**
 * Interface representing a file or folder from a Microsoft Graph drive API, including metadata listItem fields and thumbnails.
 */
export interface DriveItem {
  id: string;
  name: string;
  folder?: object;
  webUrl?: string;
  parentReference?: {
    path?: string;
    siteId?: string;
  };
  listItem?: {
    id: string;
    /** The list item's own eTag — distinct from the driveItem eTag. */
    eTag: string;
    webUrl: string;
    parentReference?: { listId?: string };
    fields?: Record<string, any>;
  };
  thumbnails?: Array<{
    id?: string;
    small?: { url: string; width: number; height: number };
    medium?: { url: string; width: number; height: number };
    large?: { url: string; width: number; height: number };
  }>;
}

/** Parses the JSON stored in a SharePoint drive item's LocationMetadata field. */
export function parseLocationMetadata(value: unknown): LocationMetadata | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
    return parsed as LocationMetadata;
  } catch {
    return null;
  }
}

/** Returns true only for finite WGS84 latitude/longitude coordinates. */
export function hasValidGps(metadata: LocationMetadata | null | undefined): metadata is LocationMetadata & { gps: NonNullable<LocationMetadata['gps']> } {
  const gps = metadata?.gps;
  return !!gps
    && Number.isFinite(gps.latitude)
    && gps.latitude >= -90
    && gps.latitude <= 90
    && Number.isFinite(gps.longitude)
    && gps.longitude >= -180
    && gps.longitude <= 180;
}

/** Maximum length of a photo note (plain text; provisional UI limit approved for PRM-551). */
export const MAX_PHOTO_NOTE_LENGTH = 500;

/** Thrown when the photo's list item changed on the server since it was read (HTTP 412). */
export class LocationMetadataConflictError extends Error {
  constructor() {
    super('This photo was changed by someone else. Its details have been refreshed; please review and try again.');
    this.name = 'LocationMetadataConflictError';
  }
}

/** Thrown when a location write would replace valid GPS that appeared since the caller prompted. */
export class LocationOverwriteNeedsConfirmationError extends Error {
  /** The valid GPS found on the fresh read, so the caller can prompt about exactly that value. */
  readonly currentGps: { latitude: number; longitude: number };
  constructor(currentGps: { latitude: number; longitude: number }) {
    super('This photo now has a location. Its details have been refreshed; confirm to replace it.');
    this.name = 'LocationOverwriteNeedsConfirmationError';
    this.currentGps = currentGps;
  }
}

/** Thrown when the GPS the user confirmed replacing differs from the fresh GPS at write time. */
export class LocationChangedSinceConfirmationError extends LocationOverwriteNeedsConfirmationError {
  constructor(currentGps: { latitude: number; longitude: number }) {
    super(currentGps);
    this.message = 'This photo\u2019s location changed after you confirmed. Its details have been refreshed; please confirm again.';
    this.name = 'LocationChangedSinceConfirmationError';
  }
}

/** Minimal Graph request chain used by {@link updateLocationMetadata} (satisfied by the SDK `Client`). */
export interface GraphMetadataRequest {
  expand(properties: string): GraphMetadataRequest;
  header(name: string, value: string): GraphMetadataRequest;
  get(): Promise<unknown>;
  patch(content: unknown): Promise<unknown>;
}
export interface GraphMetadataClient {
  api(path: string): GraphMetadataRequest;
}

export interface UpdateLocationMetadataOptions {
  /**
   * The existing GPS value the user saw and confirmed replacing. A `patch.gps` write over an existing valid
   * GPS is refused unless the fresh read's GPS equals this value (otherwise
   * {@link LocationOverwriteNeedsConfirmationError} / {@link LocationChangedSinceConfirmationError}).
   */
  confirmedExistingGps?: { latitude: number; longitude: number };
  /**
   * The note value the caller's edit was based on. When provided and the fresh read's note differs,
   * the write is refused with {@link LocationMetadataConflictError} so another user's note is not silently replaced.
   */
  expectedNote?: string;
}

/**
 * Reads and patches a photo's existing LocationMetadata JSON object while
 * preserving properties unknown to this client (including an existing gps object
 * when only a note changes, and extra gps keys when only coordinates change).
 *
 * The concurrency token is the *listItem* eTag (not the driveItem eTag), because the
 * PATCH targets `/listItem/fields`. A 412 surfaces as {@link LocationMetadataConflictError}.
 * Existing malformed JSON is rejected rather than overwritten. Guards (see
 * {@link UpdateLocationMetadataOptions}) are evaluated against the fresh read, not caller state.
 */
export async function updateLocationMetadata(
  graphClient: GraphMetadataClient,
  driveId: string,
  itemId: string,
  patch: Partial<LocationMetadata>,
  options: UpdateLocationMetadataOptions = {},
): Promise<LocationMetadata> {
  if (patch.note !== undefined && (typeof patch.note !== 'string' || patch.note.length > MAX_PHOTO_NOTE_LENGTH)) {
    throw new Error(`Photo notes must be plain text of at most ${MAX_PHOTO_NOTE_LENGTH} characters.`);
  }
  if (patch.gps && !hasValidGps({ gps: patch.gps })) {
    throw new Error('Photo location must be a valid latitude (-90 to 90) and longitude (-180 to 180).');
  }
  const item = await graphClient
    .api(`/drives/${driveId}/items/${itemId}`)
    .expand('listItem($expand=fields)')
    .get() as DriveItem;
  const raw = item.listItem?.fields?.LocationMetadata;
  const existing = parseLocationMetadata(raw);
  if (raw !== undefined && raw !== null && (typeof raw !== 'string' || !raw.trim() || !existing)) {
    throw new Error('This photo has invalid location metadata and cannot be edited safely.');
  }
  if (existing && existing.note !== undefined && typeof existing.note !== 'string') {
    throw new Error('This photo has invalid location metadata and cannot be edited safely.');
  }
  if (options.expectedNote !== undefined && (existing?.note ?? '') !== options.expectedNote) {
    throw new LocationMetadataConflictError();
  }
  if (patch.gps && hasValidGps(existing)) {
    const confirmed = options.confirmedExistingGps;
    const current = { latitude: existing.gps.latitude, longitude: existing.gps.longitude };
    if (!confirmed) throw new LocationOverwriteNeedsConfirmationError(current);
    if (confirmed.latitude !== current.latitude || confirmed.longitude !== current.longitude) {
      throw new LocationChangedSinceConfirmationError(current);
    }
  }
  const listItemETag = item.listItem?.eTag;
  if (!listItemETag) {
    throw new Error('Could not verify the photo version. Refresh the photo details and try again.');
  }
  const next: LocationMetadata = {
    ...(existing ?? {}),
    ...patch,
    ...(patch.gps ? { gps: { ...(existing?.gps ?? {}), ...patch.gps } } : {}),
  };
  if (next.note === undefined || next.note === '') delete next.note;
  try {
    await graphClient
      .api(`/drives/${driveId}/items/${itemId}/listItem/fields`)
      .header('If-Match', listItemETag)
      .patch({ LocationMetadata: JSON.stringify(next) });
  } catch (error) {
    const status = (error as { statusCode?: number } | null)?.statusCode;
    if (status === 412) throw new LocationMetadataConflictError();
    throw error;
  }
  return next;
}

// --------------------------- Folder / Item Fetching ---------------------------
/**
 * Finds a property's root document folder within a division folder in a SharePoint drive, matching first by the folder's TransactionId list field and falling back to matching the folder name against the property name.
 *
 * @param graphClient - Authenticated Microsoft Graph client.
 * @param driveId - The SharePoint drive ID.
 * @param division - The division-named top-level folder to search under.
 * @param transactionId - The transaction ID used to match the folder's TransactionId list field.
 * @param propertyName - Fallback property name (spaces replaced with underscores) used to match the folder by name if no TransactionId match is found.
 * @returns The matching folder's item ID, or null if not found.
 */
export const fetchPropertyRootFolderId = async (graphClient: any, driveId: string, division: string | undefined, transactionId: string, propertyName: string): Promise<string | null> => {
  try {
    const response = await graphClient
      ?.api(`/drives/${driveId}/root:/${division}:/children`)
      .expand('listItem')
      .get();

    let id = response.value.find((i: DriveItem) => i.listItem?.fields?.TransactionId == transactionId.toString())?.id;
    if (!id) {
      const folderName = propertyName.replace(/\s+/g, '_');
      id = response.value.find((i: DriveItem) => i.name === folderName)?.id;
    }
    return id || null;
  } catch (err) {
    console.warn('Failed to fetch property root folder', err);
    return null;
  }
};

/**
 * Lists the children of a SharePoint drive folder, expanding each item's listItem fields and thumbnails.
 *
 * @param graphClient - Authenticated Microsoft Graph client.
 * @param driveId - The SharePoint drive ID.
 * @param folderId - The folder whose children to list.
 * @returns The folder's child items, including listItem fields and thumbnails.
 */
export const listFolderItems = async (graphClient: any, driveId: string, folderId: string): Promise<DriveItem[]> => {
  let response = await graphClient.api(`/drives/${driveId}/items/${folderId}/children`).expand('listItem($expand=fields),thumbnails').get();
  const items: DriveItem[] = [...response.value];
  // A folder listing is paged. Do not publish a partial snapshot: uploads can
  // change page boundaries, and replacing map graphics with page one drops pins.
  while (response['@odata.nextLink']) {
    response = await graphClient.api(response['@odata.nextLink']).get();
    items.push(...response.value);
  }
  return items;
};

/**
 * Finds an existing child folder by name under a parent drive folder, creating it if it doesn't already exist.
 *
 * @param graphClient - Authenticated Microsoft Graph client.
 * @param driveId - The SharePoint drive ID.
 * @param parentFolderId - The parent folder to search/create within.
 * @param name - The child folder name to find or create (case-insensitive match).
 * @returns The existing or newly created child folder's item ID, or null if creation failed to return one.
 */
export const getOrCreateChildFolder = async (graphClient: any, driveId: string, parentFolderId: string, name: string): Promise<string | null> => {
  try {
    const children = await listFolderItems(graphClient, driveId, parentFolderId);
    const existing = children.find(c => c.folder && c.name.toLowerCase() === name.toLowerCase());
    if (existing) return existing.id;

    const created = await graphClient.api(`/drives/${driveId}/items/${parentFolderId}/children`).post({
      name,
      folder: {},
      '@microsoft.graph.conflictBehavior': 'rename'
    });
    return created.id || null;
  } catch (err) {
    console.error('Failed to get or create child folder', err);
    throw err;
  }
};

// --------------------------- SharePoint List Metadata Helpers ---------------------------
/**
 * Finds the list ID for a document library in a SharePoint site by matching its display name.\n\n**Note:** This is an internal helper (not exported).
 */
const getDocumentLibraryListId = async (graphClient: any, siteId: string): Promise<string | null> => {
  try {
    const lists = await graphClient.api(`/sites/${siteId}/lists`).get();
    const docLib = lists.value.find((list: any) => list.displayName === documentLibraryName);
    return docLib?.id || null;
  } catch (err) {
    console.error('Failed to get document library list ID:', err);
    return null;
  }
};

/**
 * Ensures that all metadata columns referenced in a metadata record exist in a SharePoint list, creating any missing text columns.\n\n**Note:** This is an internal helper (not exported).
 */
const ensureMetaDataColumnsExists = async (graphClient: any, siteId: string, listId: string, metaData: object) => {
  try {
    const columns = await graphClient.api(`/sites/${siteId}/lists/${listId}/columns`).get();
    const existingColumnNames = columns.value.map((col: any) => col.name);
    const missingColumns = Object.keys(metaData).filter(key => !existingColumnNames.includes(key));
    for (const columnName of missingColumns) {
      await graphClient.api(`/sites/${siteId}/lists/${listId}/columns`).post({ name: columnName, text: {} });
    }
  } catch (err) {
    console.warn('Failed ensuring metadata columns', err);
  }
};

/**
 * Tags a drive item with metadata by locating its SharePoint list and patching the corresponding list item fields.
 */
export const tagFileWithMetadata = async (graphClient: any, item: DriveItem, metaData: object) => {
  const siteId = item.parentReference?.siteId;
  const listItemId = item.listItem?.id;
  if (siteId && listItemId) {
    const listId = await getDocumentLibraryListId(graphClient, siteId);
    if (!listId) return;
    await ensureMetaDataColumnsExists(graphClient, siteId, listId, metaData);
    await graphClient.api(`/sites/${siteId}/lists/${listId}/items/${listItemId}/fields`).patch(metaData);
  }
};

// --------------------------- Upload Helpers ---------------------------
/**
 * Callback interface for reporting upload progress, status updates, and completion for batch file uploads.
 */
export interface UploadCallbacks {
  onProgress?: (file: File, percent: number) => void;
  onStatus?: (file: File, status: string) => void;
  onComplete?: (file: File, success: boolean) => void;
  afterAll?: () => void;
}

/**
 * Uploads an array of files to a SharePoint drive folder, tagging each with metadata and reporting progress/status via callbacks.
 */
export const uploadFilesToFolder = async (graphClient: any, driveId: string, folderId: string, files: File[], metaData: Record<string, any>, cb: UploadCallbacks = {}) => {
  for (const file of files) {
    cb.onStatus?.(file, 'Uploading...');
    const reader = new FileReader();
    await new Promise<void>((resolve) => {
      reader.onload = async () => {
        try {
          await graphClient
            .api(`/drives/${driveId}/items/${folderId}:/${file.name}:/content`)
            .put(file, { headers: { 'Content-Type': file.type || 'application/octet-stream' } });
          const uploadedItem = await graphClient
            .api(`/drives/${driveId}/items/${folderId}:/${file.name}`)
            .expand('listItem,thumbnails')
            .get();
          await tagFileWithMetadata(graphClient, uploadedItem, metaData);
          cb.onStatus?.(file, '✅ Uploaded');
          cb.onProgress?.(file, 100);
          cb.onComplete?.(file, true);
        } catch (err: any) {
          console.error('Upload failed', err);
            cb.onStatus?.(file, `❌ ${err.message || 'Failed'}`);
            cb.onProgress?.(file, 0);
            cb.onComplete?.(file, false);
        } finally {
          resolve();
        }
      };
      reader.onprogress = (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          cb.onProgress?.(file, percent);
        }
      };
      reader.onerror = () => {
        cb.onStatus?.(file, '❌ File read error');
        cb.onProgress?.(file, 0);
        cb.onComplete?.(file, false);
        resolve();
      };
      reader.readAsArrayBuffer(file);
    });
  }
  cb.afterAll?.();
};

/**
 * Uploads files to a SharePoint drive folder, extracting GPS EXIF metadata from images and tagging the uploaded items with LocationMetadata.
 */
export const uploadFilesToFolderWithGps = async (
  graphClient: any,
  driveId: string,
  folderId: string,
  files: File[],
  transactionId?: string,
  callbacks: UploadCallbacks = {}
) => {
  for (const file of files) {
    const isImage = file.type.startsWith('image/');
    let gpsJson: string | null = null;
    if (isImage) {
      try { gpsJson = await extractGpsMetadataJson(file); } catch { gpsJson = null; }
    }
    callbacks.onStatus?.(file, 'Uploading...');
    const reader = new FileReader();
    await new Promise<void>((resolve) => {
      reader.onload = async () => {
        try {
          await graphClient
            .api(`/drives/${driveId}/items/${folderId}:/${file.name}:/content`)
            .put(file, { headers: { 'Content-Type': file.type || 'application/octet-stream' } });
          const uploadedItem = await graphClient
            .api(`/drives/${driveId}/items/${folderId}:/${file.name}`)
            .expand('listItem,thumbnails')
            .get();
          const meta: Record<string, any> = {};
          if (transactionId) meta.TransactionId = transactionId;
          if (gpsJson) meta.LocationMetadata = gpsJson; // JSON (ImageMetadataPayload) stored in LocationMetadata column
          if (Object.keys(meta).length) {
            await tagFileWithMetadata(graphClient, uploadedItem, meta);
          }
          callbacks.onStatus?.(file, '✅ Uploaded');
          callbacks.onProgress?.(file, 100);
          callbacks.onComplete?.(file, true);
        } catch (err: any) {
          callbacks.onStatus?.(file, `❌ ${err.message || 'Failed'}`);
          callbacks.onProgress?.(file, 0);
          callbacks.onComplete?.(file, false);
        } finally { resolve(); }
      };
      reader.onprogress = (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          callbacks.onProgress?.(file, percent);
        }
      };
      reader.onerror = () => {
        callbacks.onStatus?.(file, '❌ File read error');
        callbacks.onProgress?.(file, 0);
        callbacks.onComplete?.(file, false);
        resolve();
      };
      reader.readAsArrayBuffer(file);
    });
  }
  callbacks.afterAll?.();
};

// --------------------------- File Type & Preview Helpers (DriveItem based) ---------------------------
/**
 * Determines whether a DriveItem represents a video file by checking its listItem file type or file extension.
 */
export const isVideoFile = (item: DriveItem) => {
  const videoMimeTypes = ['video/mp4', 'video/quicktime', 'video/mpeg'];
  const mimeType = item.listItem?.fields?.File_x0020_Type ? `video/${item.listItem.fields.File_x0020_Type.toLowerCase()}` : undefined;
  const ext = item.name.split('.').pop()?.toLowerCase();
  return videoMimeTypes.includes(mimeType || '') || ['mp4', 'mov', 'mpeg'].includes(ext || '');
};

/**
 * Determines whether a DriveItem represents an image file by checking its extension or listItem file type.
 */
export const isImageFile = (item: DriveItem) => {
  const ext = item.name.split('.').pop()?.toLowerCase();
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic'];
  if (ext && imageExts.includes(ext)) return true;
  const fileType = item.listItem?.fields?.File_x0020_Type?.toLowerCase();
  return !!(fileType && imageExts.includes(fileType));
};

/**
 * Generates a SharePoint embed URL for a DriveItem, using the ETag UniqueId, or null if unavailable.
 */
export const getSharePointEmbedUrl = (item: DriveItem) => {
  if (item.listItem?.eTag) {
    try {
      const [etag] = JSON.parse(item.listItem.eTag).split(',');
      const uniqueId = etag || item.listItem?.fields?.UniqueId || item.listItem?.id;
      if (!uniqueId) return null;
      return `https://agreserves.sharepoint.com/sites/DevOps/_layouts/15/embed.aspx?UniqueId=${uniqueId}&embed=%7B%22ust%22%3Afalse%2C%22hv%22%3A%22CopyEmbedCode%22%7D&referrer=StreamWebApp&referrerScenario=EmbedDialog.Create`;
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Returns the SharePoint web URL for a DriveItem, checking listItem and root-level webUrl fields.
 */
export const getSharePointFileUrl = (item: DriveItem) => {
  if (item.listItem?.webUrl) return item.listItem.webUrl;
  if (item.webUrl) return item.webUrl;
  return null;
};

/**
 * Returns the largest available thumbnail URL for a DriveItem, falling back to a SharePoint file URL, or null.
 */
export const getImagePreviewUrl = (item: DriveItem) => {
  return (
    item.thumbnails?.[0]?.large?.url ||
    item.thumbnails?.[0]?.medium?.url ||
    item.thumbnails?.[0]?.small?.url ||
    getSharePointFileUrl(item)
  );
};

/**
 * Moves a drive item to a different folder within the same SharePoint drive.
 */
export const moveDriveItem = async (graphClient: any, driveId: string, itemId: string, destFolderId: string) => {
  await graphClient.api(`/drives/${driveId}/items/${itemId}`).patch({ parentReference: { id: destFolderId } });
};

/**
 * Shows a Mantine notification confirming that a file was moved into a destination folder.
 */
export const notifyMove = (srcName: string, destName: string) => {
  notifications.show({ title: 'Moved', message: `${srcName} moved into ${destName}`, color: 'green' });
};

/**
 * Shows a Mantine notification reporting that a file move operation failed.
 */
export const notifyMoveFailed = (message: string) => {
  notifications.show({ title: 'Move failed', message, color: 'red' });
};

// --------------------------- Download Helpers ---------------------------


/**
 * Trigger a browser download for a drive item by streaming through the
 * Graph API /content endpoint. This uses the user's existing auth token
 * and respects org Conditional Access / DLP policies.
 */
export const downloadDriveItem = async (
  graphClient: any,
  driveId: string,
  item: DriveItem
): Promise<void> => {
  try {
    const blob: Blob = await graphClient
      .api(`/drives/${driveId}/items/${item.id}/content`)
      .responseType(ResponseType.BLOB)
      .get();

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = item.name;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error('downloadDriveItem failed', err);
    notifications.show({ title: 'Download failed', message: 'Could not download the file.', color: 'red' });
  }
};

// Retrieve metadata JSON objects from the Pictures folder under a given property root folder
/**
 * Records parsed image metadata from a Pictures folder, exposing the original JSON string and parsed ImageMetadataPayload.
 */
export interface PictureMetadataRecord {
  itemId: string;
  name: string;
  raw: string; // original JSON string
  metadata: LocationMetadata; // parsed
}

/**
 * Finds a "Pictures" folder under a property's root folder and extracts image metadata JSON from all files inside it.
 */
export const getPicturesFolderImageMetadata = async (
  graphClient: any,
  driveId: string,
  propertyRootFolderId: string,
  picturesFolderName = 'Pictures'
): Promise<PictureMetadataRecord[]> => {
  if (!graphClient || !driveId || !propertyRootFolderId) return [];
  try {
    // First find the Pictures folder
    const rootChildren = await graphClient
      .api(`/drives/${driveId}/items/${propertyRootFolderId}/children`)
      .expand('listItem')
      .get();
    const picturesFolder = rootChildren.value.find((c: any) => c.folder && c.name?.toLowerCase() === picturesFolderName.toLowerCase());
    if (!picturesFolder) return [];

    // List items inside Pictures folder (expand listItem to access fields)
    const picsChildren = await graphClient
      .api(`/drives/${driveId}/items/${picturesFolder.id}/children`)
      .expand('listItem,thumbnails')
      .get();

    const results: PictureMetadataRecord[] = [];
    for (const item of picsChildren.value as DriveItem[]) {
      const jsonStr = item.listItem?.fields?.LocationMetadata;
      if (!jsonStr || typeof jsonStr !== 'string') continue;
      try {
        const parsed = parseLocationMetadata(jsonStr);
        if (!parsed) continue;
        results.push({ itemId: item.id, name: item.name, raw: jsonStr, metadata: parsed });
      } catch {
        // ignore malformed JSON
      }
    }
    return results;
  } catch (err) {
    console.warn('getPicturesFolderImageMetadata failed', err);
    return [];
  }
};

// Convenience: derive property root from transaction context then fetch metadata
/**
 * Convenience wrapper that locates a property's root folder then fetches image metadata from its Pictures subfolder.
 */
export const getTransactionPicturesImageMetadata = async (
  graphClient: any,
  driveId: string,
  division: string | undefined,
  transactionId: string,
  propertyName: string,
  picturesFolderName = 'Pictures'
): Promise<PictureMetadataRecord[]> => {
  const rootId = await fetchPropertyRootFolderId(graphClient, driveId, division, transactionId, propertyName);
  if (!rootId) return [];
  return getPicturesFolderImageMetadata(graphClient, driveId, rootId, picturesFolderName);
};

// Office file helpers
/**
 * File extensions recognized as Microsoft Word documents (doc, docx).
 */
export const OFFICE_WORD_EXT = ['doc', 'docx'];
/**
 * File extensions recognized as Microsoft Excel workbooks (xls, xlsx, xlsm, xlsb, csv).
 */
export const OFFICE_EXCEL_EXT = ['xls', 'xlsx', 'xlsm', 'xlsb', 'csv'];
/**
 * File extensions recognized as Microsoft PowerPoint presentations (ppt, pptx, pptm).
 */
export const OFFICE_PPT_EXT = ['ppt', 'pptx', 'pptm'];

/**
 * Determines whether a DriveItem is a Microsoft Word document by checking its file extension.
 */
export const isOfficeWordFile = (item: DriveItem) => {
  if (item.folder) return false;
  const ext = item.name.split('.').pop()?.toLowerCase();
  return !!ext && OFFICE_WORD_EXT.includes(ext);
};
/**
 * Determines whether a DriveItem is a Microsoft Excel file by checking its file extension.
 */
export const isOfficeExcelFile = (item: DriveItem) => {
  if (item.folder) return false;
  const ext = item.name.split('.').pop()?.toLowerCase();
  return !!ext && OFFICE_EXCEL_EXT.includes(ext);
};
/**
 * Determines whether a DriveItem is a Microsoft PowerPoint file by checking its file extension.
 */
export const isOfficePowerPointFile = (item: DriveItem) => {
  if (item.folder) return false;
  const ext = item.name.split('.').pop()?.toLowerCase();
  return !!ext && OFFICE_PPT_EXT.includes(ext);
};
/**
 * Returns true if a DriveItem is any Microsoft Office file (Word, Excel, or PowerPoint).
 */
export const isOfficeFile = (item: DriveItem) => (
  isOfficeWordFile(item) || isOfficeExcelFile(item) || isOfficePowerPointFile(item)
);

// Build a deep link for opening in native desktop Office application (ms-word / ms-excel / ms-powerpoint)
// Returns null if no suitable web URL exists.
/**
 * Builds a deep link for opening a drive item in the native desktop Office application, or null if no web URL exists.
 */
export const buildDesktopOfficeUrl = (item: DriveItem): string | null => {
  const webUrl = getSharePointFileUrl(item);
  if (!webUrl) return null;
  if (isOfficeWordFile(item)) return 'ms-word:ofe|u|' + webUrl;
  if (isOfficeExcelFile(item)) return 'ms-excel:ofe|u|' + webUrl;
  if (isOfficePowerPointFile(item)) return 'ms-powerpoint:ofe|u|' + webUrl;
  return null;
};

// Attempt to open an Office file in the native desktop client via protocol handler, with timed web fallback.
// NOTE: Browsers intentionally do not expose a reliable success/failure event for custom protocol attempts.
// Strategy: trigger protocol navigation; if the page still has focus after a delay, assume failure and open web URL.
/**
 * Attempts to open an Office file in the native desktop client via protocol handler, with an implicit timed web fallback.
 */
export const openOfficeInDesktop = (item: DriveItem) => {
  const webUrl = getSharePointFileUrl(item);
  if (!webUrl) return;
  const desktopUrl = buildDesktopOfficeUrl(item);
  const a = document.createElement('a');
  if(desktopUrl != null) {
    // @ts-ignore
    a.href = desktopUrl;
    document.body.appendChild(a);
    a.click();
  }
};

// filepath: /Users/nrees/WorkProjects/Web/prismweb/packages/ui/src/driveUtils.ts
// ...existing code...
/**
 * Callback interface for reporting status and completion of a single-file upload.
 */
export interface SingleUploadCallbacks {
  onStatus?: (status: string) => void;
  onComplete?: (success: boolean) => void;
}

/**
 * Uploads a single file to a SharePoint drive folder and tags it with metadata, reporting status via callbacks.
 */
export const uploadFileWithMetadata = async (
  graphClient: any,
  driveId: string,
  folderId: string,
  file: File,
  metaData: Record<string, any>,
  cb: SingleUploadCallbacks = {}
) => {
  try {
    cb.onStatus?.('Uploading...');
    await graphClient
      .api(`/drives/${driveId}/items/${folderId}:/${file.name}:/content`)
      .put(file, { headers: { 'Content-Type': file.type || 'application/octet-stream' } });
    const uploadedItem = await graphClient
      .api(`/drives/${driveId}/items/${folderId}:/${file.name}`)
      .expand('listItem,thumbnails')
      .get();
    if (Object.keys(metaData).length) {
      await tagFileWithMetadata(graphClient, uploadedItem, metaData);
    }
    cb.onStatus?.('✅ Uploaded');
    cb.onComplete?.(true);
  } catch (err: any) {
    console.error('uploadFileWithMetadata failed', err);
    cb.onStatus?.(`❌ ${err.message || 'Failed'}`);
    cb.onComplete?.(false);
  }
};
